/**
 * DM Chat Service
 * API calls for direct messaging conversations and messages
 */

import { apiClient } from "@/lib/api-client"
import type {
  DmConversation,
  DmConversationDetailResponse,
  DmConversationListResponse,
  DmMessage,
} from "../types/dm-chat.types"

interface ApiResponse<T> {
  success: boolean
  data: T
  message: string
}

export async function createOrGetConversation(targetUserId: string): Promise<DmConversation> {
  const response = await apiClient.post<ApiResponse<DmConversation>>("/api/conversations", {
    targetUserId,
  })
  return response.data.data
}

/**
 * Create or get the booked-session "Chat with Expert" DM. Backend derives
 * the expert from the user's existing SessionBooking. Pass either ritualId
 * or bookingId — at least one is required.
 */
export async function createExpertChat(opts: {
  ritualId?: string
  bookingId?: string
}): Promise<DmConversation> {
  const response = await apiClient.post<ApiResponse<DmConversation>>(
    "/api/conversations/expert-chat",
    opts,
  )
  return response.data.data
}

export async function listConversations(page = 1, limit = 20): Promise<DmConversationListResponse> {
  const response = await apiClient.get<ApiResponse<DmConversationListResponse>>(
    "/api/conversations",
    { params: { page, limit, hasMessages: true } },
  )
  return response.data.data
}

export async function getConversation(
  id: string,
  messagePage = 1,
  messageLimit = 50,
): Promise<DmConversationDetailResponse> {
  const response = await apiClient.get<ApiResponse<DmConversationDetailResponse>>(
    `/api/conversations/${id}`,
    {
      params: { messagePage, messageLimit },
    },
  )
  return response.data.data
}

export async function deleteConversation(id: string): Promise<void> {
  await apiClient.delete(`/api/conversations/${id}`)
}

export async function markAsRead(conversationId: string): Promise<void> {
  await apiClient.post(`/api/conversations/${conversationId}/read`)
}

export async function sendMessage(conversationId: string, content: string): Promise<DmMessage> {
  const response = await apiClient.post<ApiResponse<DmMessage>>(
    `/api/conversations/${conversationId}/messages`,
    { content },
  )
  return response.data.data
}

export async function editMessage(
  conversationId: string,
  messageId: string,
  content: string,
): Promise<DmMessage> {
  const response = await apiClient.patch<ApiResponse<DmMessage>>(
    `/api/conversations/${conversationId}/messages/${messageId}`,
    { content },
  )
  return response.data.data
}

export async function deleteMessage(conversationId: string, messageId: string): Promise<void> {
  await apiClient.delete(`/api/conversations/${conversationId}/messages/${messageId}`)
}
